import { IFlow, INode, IPage, ITypeNode } from '../Context/FormBuilderContext';
import { getDefaultNodeData } from '../utils/nodeDefaults';
import { getNodesForPage, getPageById } from '../utils/pageUtils';

export interface FormBuilderState {
  form: {
    id: string;
    name: string;
    flows: IFlow[];
    status: 'draft' | 'published';
    activeFlows: string[];
    publishType: 'internal' | 'external';
    newFormNodesPublished: INode[];
    newFormNodes: INode[];
    pages?: IPage[];
    updatedAt: string;
    formStyle: {
      backgroundColor?: string;
      backgroundImage?: string;
      pageOrientation?: string;
    };
    formSettings: {
      successMessage?: string;
      loadingMessage?: string;
      agentSessionMode?: boolean;
    };
  };
  varInputNodes: {
    id: string;
    label: string;
    type: string;
    flowName: string;
    flowId: string;
  }[];
  varOutputNodes: {
    id: string;
    label: string;
    type: string;
    flowName: string;
    flowId: string;
  }[];
}

export enum ActionTypes {
  UPDATE_FORM = 'UPDATE_FORM',
  ADD_NODES = 'ADD_NODES',
  REMOVE_NODES = 'REMOVE_NODES',
  UPDATE_NODES_LAYOUT = 'UPDATE_NODES_LAYOUT',
  UPDATE_NODES_DATA = 'UPDATE_NODES_DATA',
  UPDATE_FORM_STATUS = 'UPDATE_FORM_STATUS',
  UPDATE_PUBLISH = 'UPDATE_PUBLISH',
  UPDATE_ACTIVE_FLOWS = 'UPDATE_ACTIVE_FLOWS',
  UPDATE_FORM_STYLES = 'UPDATE_FORM_STYLES',
  UPDATE_FORM_SETTINGS = 'UPDATE_FORM_SETTINGS',
  DUPLICATE_NODE = 'DUPLICATE_NODE',
  INITIALIZE_PAGES = 'INITIALIZE_PAGES',
  ADD_PAGE = 'ADD_PAGE',
  REMOVE_PAGE = 'REMOVE_PAGE',
  MOVE_NODE_TO_PAGE = 'MOVE_NODE_TO_PAGE',
  AUTO_POPULATE_FORM = 'AUTO_POPULATE_FORM',
}

export type IActionType =
  | {
      type: ActionTypes.UPDATE_FORM;
      payload: {
        form: FormBuilderState['form'];
        varInputNodes: FormBuilderState['varInputNodes'];
        varOutputNodes: FormBuilderState['varOutputNodes'];
      };
    }
  | {
      type: ActionTypes.ADD_NODES;
      payload: {
        type: ITypeNode;
        currentPageId?: string | null;
      };
    }
  | { type: ActionTypes.REMOVE_NODES; payload: string }
  | {
      type: ActionTypes.UPDATE_NODES_LAYOUT;
      payload: { id: string; updates: { layout: INode['layout'] } };
    }
  | {
      type: ActionTypes.UPDATE_NODES_DATA;
      payload: { id: string; updates: { data: INode['data'] } };
    }
  | { type: ActionTypes.UPDATE_FORM_STATUS; payload: 'draft' | 'published' }
  | {
      type: ActionTypes.UPDATE_PUBLISH;
      payload: {
        status: 'published' | 'draft';
        publishType: 'internal' | 'external';
        publishedNodes: any;
      };
    }
  | {
      type: ActionTypes.UPDATE_ACTIVE_FLOWS;
      payload: {
        activeFlows: string[];
        varInputNodes: FormBuilderState['varInputNodes'];
        varOutputNodes: FormBuilderState['varOutputNodes'];
        flows: IFlow[];
      };
    }
  | {
      type: ActionTypes.UPDATE_FORM_STYLES;
      payload: {
        backgroundColor?: string;
        backgroundImage?: string;
        pageOrientation?: string;
      };
    }
  | {
      type: ActionTypes.UPDATE_FORM_SETTINGS;
      payload: {
        successMessage?: string;
        loadingMessage?: string;
        agentSessionMode?: boolean;
      };
    }
  | {
      type: ActionTypes.DUPLICATE_NODE;
      payload: { node: INode; currentPageId?: string | null };
    }
  | {
      type: ActionTypes.INITIALIZE_PAGES;
    }
  | {
      type: ActionTypes.ADD_PAGE;
      payload: { name?: string; pageId?: string };
    }
  | {
      type: ActionTypes.REMOVE_PAGE;
      payload: { pageId: string };
    }
  | {
      type: ActionTypes.MOVE_NODE_TO_PAGE;
      payload: { nodeId: string; targetPageId: string };
    }
  | {
      type: ActionTypes.AUTO_POPULATE_FORM;
      payload: {
        newFormNodes: INode[];
        pages: IPage[];
        formSettings: {
          successMessage?: string;
          loadingMessage?: string;
          agentSessionMode?: boolean;
        };
      };
    };

export function formBuilderReducer(
  state: FormBuilderState,
  action: IActionType,
): FormBuilderState {
  switch (action.type) {
    case ActionTypes.UPDATE_FORM:
      const formPages = action.payload.form.pages;

      const hasNewNodes =
        action.payload.form.newFormNodes &&
        action.payload.form.newFormNodes.length > 0;
      const hasPages = formPages && formPages.length > 0;

      let migratedPages = formPages;

      const uuid = crypto.randomUUID();

      if (!hasPages) {
        const newPage: IPage = {
          id: uuid,
          name: 'Page 1',
          nodeIds: hasNewNodes
            ? action.payload.form.newFormNodes.map(node => node.id)
            : [],
        };
        migratedPages = [newPage];
      }

      return {
        ...state,
        form: {
          ...action.payload.form,
          pages: migratedPages,
        },
        varInputNodes: action.payload.varInputNodes,
        varOutputNodes: action.payload.varOutputNodes,
      };

    case ActionTypes.ADD_NODES:
      const nodeUuid = crypto.randomUUID();
      const id = `${action.payload.type}-${nodeUuid}`;

      let initialY = 0;
      if (
        state.form.pages &&
        state.form.pages.length > 0 &&
        action.payload.currentPageId
      ) {
        const currentPage = getPageById(
          action.payload.currentPageId,
          state.form.pages,
        );
        const currentPageNodes = getNodesForPage(
          currentPage,
          state.form.newFormNodes,
        );

        if (currentPageNodes.length > 0) {
          initialY = Math.max(
            ...currentPageNodes.map(item => item.layout.y + item.layout.h),
            0,
          );
        }
      } else if (state.form.pages && state.form.pages.length > 0) {
        const firstPage = state.form.pages[0];
        const firstPageNodes = getNodesForPage(
          firstPage,
          state.form.newFormNodes,
        );
        if (firstPageNodes.length > 0) {
          initialY = Math.max(
            ...firstPageNodes.map(item => item.layout.y + item.layout.h),
            0,
          );
        }
      } else {
        initialY = Math.max(
          ...state.form.newFormNodes.map(item => item.layout.y + item.layout.h),
          0,
        );
      }

      const node: INode = {
        id,
        type: action.payload.type,
        data: {} as INode['data'],
        layout: {
          i: id,
          x: 0,
          y: initialY,
          w: 0,
          h: 0,
          minW: 0,
          minH: 0,
        },
      };

      if (action.payload.type === ITypeNode.TITLE) {
        node.data = getDefaultNodeData(ITypeNode.TITLE);

        node.layout.w = 12;
        node.layout.h = 2;
        node.layout.minW = 3;
        node.layout.minH = 2;
      }

      if (action.payload.type === ITypeNode.SHORT_TEXT) {
        node.data = getDefaultNodeData(ITypeNode.SHORT_TEXT);
        node.layout.w = 12;
        node.layout.h = 2;
        node.layout.minW = 3;
        node.layout.minH = 2;
      }

      if (action.payload.type === ITypeNode.IMAGE) {
        node.data = getDefaultNodeData(ITypeNode.IMAGE);
        node.layout.w = 12;
        node.layout.h = 4;
        node.layout.minW = 3;
        node.layout.minH = 4;
      }

      if (action.payload.type === ITypeNode.INPUT) {
        node.data = getDefaultNodeData(ITypeNode.INPUT);
        node.layout.w = 12;
        node.layout.h = 6;
        node.layout.minW = 3;
        node.layout.minH = 6;
      }

      if (action.payload.type === ITypeNode.OUTPUT) {
        node.data = getDefaultNodeData(ITypeNode.OUTPUT);
        node.layout.w = 12;
        node.layout.h = 5;
        node.layout.minW = 3;
        node.layout.minH = 5;
      }

      if (action.payload.type === ITypeNode.LIST) {
        node.data = getDefaultNodeData(ITypeNode.LIST);
        node.layout.w = 12;
        node.layout.h = 4;
        node.layout.minW = 3;
        node.layout.minH = 4;
      }

      if (action.payload.type === ITypeNode.SUBMIT) {
        node.data = getDefaultNodeData(ITypeNode.SUBMIT);
        node.layout.w = 12;
        node.layout.h = 2;
        node.layout.minW = 2;
        node.layout.minH = 2;
      }

      let finalPages = state.form.pages || [];

      if (!finalPages || finalPages.length === 0) {
        const pageUuid = crypto.randomUUID();
        const newPage: IPage = {
          id: pageUuid,
          name: 'Page 1',
          nodeIds: [id],
        };
        finalPages = [newPage];
      } else {
        const targetPageId = action.payload.currentPageId || finalPages[0]?.id;
        finalPages = finalPages.map(page => {
          if (page.id === targetPageId) {
            return { ...page, nodeIds: [...page.nodeIds, id] };
          }
          return page;
        });
      }

      return {
        ...state,
        form: {
          ...state.form,
          newFormNodes: [...state.form.newFormNodes, node],
          pages: finalPages,
          updatedAt: new Date().toISOString(),
        },
      };

    case ActionTypes.REMOVE_NODES:
      const pagesAfterNodeRemoval = state.form.pages?.map(page => ({
        ...page,
        nodeIds: page.nodeIds.filter(nodeId => nodeId !== action.payload),
      }));

      return {
        ...state,
        form: {
          ...state.form,
          newFormNodes: state.form.newFormNodes.filter(
            item => item.id !== action.payload,
          ),
          pages: pagesAfterNodeRemoval,
        },
      };

    case ActionTypes.UPDATE_NODES_LAYOUT:
      return {
        ...state,
        form: {
          ...state.form,
          newFormNodes: state.form.newFormNodes.map(item =>
            item.id === action.payload.id
              ? { ...item, layout: action.payload.updates.layout }
              : item,
          ),
          updatedAt: new Date().toISOString(),
        },
      };

    case ActionTypes.UPDATE_FORM_STATUS:
      return {
        ...state,
        form: {
          ...state.form,
          status: action.payload,
          updatedAt: new Date().toISOString(),
        },
      };

    case ActionTypes.UPDATE_ACTIVE_FLOWS:
      return {
        ...state,
        form: {
          ...state.form,
          activeFlows: action.payload.activeFlows,
          flows: action.payload.flows,
          updatedAt: new Date().toISOString(),
        },
        varInputNodes: action.payload.varInputNodes,
        varOutputNodes: action.payload.varOutputNodes,
      };

    case ActionTypes.UPDATE_PUBLISH:
      return {
        ...state,
        form: {
          ...state.form,
          status: action.payload.status,
          publishType: action.payload.publishType,
          newFormNodesPublished: action.payload.publishedNodes,
          updatedAt: new Date().toISOString(),
        },
      };

    case ActionTypes.UPDATE_NODES_DATA:
      return {
        ...state,
        form: {
          ...state.form,
          newFormNodes: state.form.newFormNodes.map(item =>
            item.id === action.payload.id
              ? { ...item, data: action.payload.updates.data }
              : item,
          ),
          updatedAt: new Date().toISOString(),
        },
      };

    case ActionTypes.UPDATE_FORM_STYLES:
      return {
        ...state,
        form: {
          ...state.form,
          formStyle: {
            ...state.form.formStyle,
            ...action.payload,
          },
        },
      };

    case ActionTypes.UPDATE_FORM_SETTINGS:
      return {
        ...state,
        form: {
          ...state.form,
          formSettings: action.payload,
        },
      };

    case ActionTypes.DUPLICATE_NODE:
      const originalNode = action.payload.node;
      const newId = crypto.randomUUID();

      let duplicateY = 0;
      if (
        state.form.pages &&
        state.form.pages.length > 0 &&
        action.payload.currentPageId
      ) {
        const currentPage = getPageById(
          action.payload.currentPageId,
          state.form.pages,
        );
        const currentPageNodes = getNodesForPage(
          currentPage,
          state.form.newFormNodes,
        );
        if (currentPageNodes.length > 0) {
          duplicateY = Math.max(
            ...currentPageNodes.map(item => item.layout.y + item.layout.h),
            0,
          );
        }
      } else if (state.form.pages && state.form.pages.length > 0) {
        const firstPage = state.form.pages[0];
        const firstPageNodes = getNodesForPage(
          firstPage,
          state.form.newFormNodes,
        );
        if (firstPageNodes.length > 0) {
          duplicateY = Math.max(
            ...firstPageNodes.map(item => item.layout.y + item.layout.h),
            0,
          );
        }
      } else {
        duplicateY = Math.max(
          ...state.form.newFormNodes.map(item => item.layout.y + item.layout.h),
          0,
        );
      }

      const duplicatedNode: INode = {
        ...originalNode,
        id: newId,
        layout: {
          ...originalNode.layout,
          i: newId,
          x: originalNode.layout.x,
          y: duplicateY,
        },
      };

      let finalPagesAfterDuplicate = state.form.pages || [];

      if (!finalPagesAfterDuplicate || finalPagesAfterDuplicate.length === 0) {
        const pageUuid = crypto.randomUUID();
        const newPage: IPage = {
          id: pageUuid,
          name: 'Page 1',
          nodeIds: [newId],
        };
        finalPagesAfterDuplicate = [newPage];
      } else {
        const targetPageId =
          action.payload.currentPageId || finalPagesAfterDuplicate[0]?.id;
        finalPagesAfterDuplicate = finalPagesAfterDuplicate.map(page => {
          if (page.id === targetPageId) {
            return { ...page, nodeIds: [...page.nodeIds, newId] };
          }
          return page;
        });
      }

      return {
        ...state,
        form: {
          ...state.form,
          newFormNodes: [...state.form.newFormNodes, duplicatedNode],
          pages: finalPagesAfterDuplicate,
          updatedAt: new Date().toISOString(),
        },
      };

    case ActionTypes.INITIALIZE_PAGES:
      if (
        (!state.form.pages || state.form.pages.length === 0) &&
        state.form.newFormNodes.length > 0
      ) {
        const pageId = crypto.randomUUID();
        const newPage: IPage = {
          id: pageId,
          name: 'Page 1',
          nodeIds: state.form.newFormNodes.map(node => node.id),
        };

        return {
          ...state,
          form: {
            ...state.form,
            pages: [newPage],
            updatedAt: new Date().toISOString(),
          },
        };
      }
      return state;

    case ActionTypes.ADD_PAGE:
      const currentPages = state.form.pages || [];

      if (currentPages.length >= 2) {
        return state;
      }

      const pageNumber = currentPages.length + 1;
      const newPageId = action.payload?.pageId || crypto.randomUUID();

      const newPage: IPage = {
        id: newPageId,
        name: action.payload?.name || `Page ${pageNumber}`,
        nodeIds: [],
      };

      return {
        ...state,
        form: {
          ...state.form,
          pages: [...currentPages, newPage],
          updatedAt: new Date().toISOString(),
        },
      };

    case ActionTypes.REMOVE_PAGE:
      const pagesAfterRemoval = (state.form.pages || []).filter(
        page => page.id !== action.payload.pageId,
      );

      const pageToRemove = (state.form.pages || []).find(
        page => page.id === action.payload.pageId,
      );

      const nodeIdsToRemove = pageToRemove?.nodeIds || [];
      const remainingNodes = state.form.newFormNodes.filter(
        node => !nodeIdsToRemove.includes(node.id),
      );

      return {
        ...state,
        form: {
          ...state.form,
          pages: pagesAfterRemoval,
          newFormNodes: remainingNodes,
          updatedAt: new Date().toISOString(),
        },
      };

    case ActionTypes.MOVE_NODE_TO_PAGE:
      const { nodeId, targetPageId } = action.payload;

      return {
        ...state,
        form: {
          ...state.form,
          pages: (state.form.pages || []).map(page => {
            const nodeIdsWithoutNode = page.nodeIds.filter(id => id !== nodeId);

            if (page.id === targetPageId) {
              if (!nodeIdsWithoutNode.includes(nodeId)) {
                return { ...page, nodeIds: [...nodeIdsWithoutNode, nodeId] };
              }
            }

            return { ...page, nodeIds: nodeIdsWithoutNode };
          }),
          updatedAt: new Date().toISOString(),
        },
      };

    case ActionTypes.AUTO_POPULATE_FORM:
      return {
        ...state,
        form: {
          ...state.form,
          newFormNodes: action.payload.newFormNodes,
          pages: action.payload.pages,
          formSettings: action.payload.formSettings,
          updatedAt: new Date().toISOString(),
        },
      };

    default:
      return state;
  }
}
